import React from "react";
import { PageIdType } from "__generated__/globalTypes";
import {
  STATIC_PAGE_QUERY,
  StaticPageQuery,
  StaticPageQueryVariables,
} from "components/StaticPageTemplate";
import Layout from "components/common/Layout";
import NestedStaticPageRoute from "components/common/NestedStaticPageRoute";
import ProductBundles, {
  PRODUCT_BUNDLES_QUERY,
  ProductBundlesQuery,
  ProductBundlesQueryVariables,
} from "components/courses/Catalog/ProductBundles";
import { SeoPageProps } from "helpers/seo";
import { lookupStaticPage } from "helpers/staticPageLookup";
import { withEnhancedServerSideProps } from "helpers/withEnhancedProps";
import { useRouterQuery } from "hooks/useRouterQuery";

interface CourseCatalogCategoryPageProps extends SeoPageProps {
  isCourseCategory: boolean;
  staticPage?: StaticPageQuery["page"];
}

const CourseCatalogCategoryPage = ({
  seo,
  isCourseCategory,
  staticPage,
}: CourseCatalogCategoryPageProps) => {
  const {
    routerQuery: { slug },
  } = useRouterQuery<{ slug: string }>();

  // If not a course category but WordPress page exists, render WordPress content
  if (!isCourseCategory && staticPage) {
    return <NestedStaticPageRoute seo={seo} staticPage={staticPage} />;
  }

  // Render course catalog category (getServerSideProps ensures this only runs when courses exist)
  return (
    <Layout initialMeta={seo}>
      <ProductBundles category={slug} />
    </Layout>
  );
};

export default CourseCatalogCategoryPage;

export const getServerSideProps = withEnhancedServerSideProps<CourseCatalogCategoryPageProps>(
  async ctx => {
    if (ctx.isInitialRequest) {
      const slug = Array.isArray(ctx.query.slug) ? ctx.query.slug[0] : ctx.query.slug || "";

      const [staticPageResult, courseCategoryResult] = await Promise.all([
        ctx.apolloClient.query<StaticPageQuery, StaticPageQueryVariables>({
          query: STATIC_PAGE_QUERY,
          variables: { idType: PageIdType.URI, id: "course-catalog" },
        }),
        ctx.apolloClient.query<ProductBundlesQuery, ProductBundlesQueryVariables>({
          query: PRODUCT_BUNDLES_QUERY,
          variables: { slug },
        }),
      ]);

      const isCourseCategory =
        (courseCategoryResult?.data?.productCategory?.products?.nodes?.length ?? 0) > 0;

      // If not a course category, try to find WordPress page
      let wordpressPage = null;
      if (!isCourseCategory) {
        const pageResult = await lookupStaticPage(
          ctx.apolloClient,
          `/course-catalog/${slug}`,
          "/course-catalog/subjects/"
        );
        wordpressPage = pageResult?.staticPage || null;
      }

      // Only return 404 if BOTH not a course category AND WordPress page don't exist
      if (!isCourseCategory && !wordpressPage) {
        return { notFound: true };
      }

      return {
        props: {
          seo: wordpressPage?.seo || staticPageResult?.data?.page?.seo || null,
          title: wordpressPage?.title || staticPageResult?.data?.page?.title || "",
          isCourseCategory,
          staticPage: wordpressPage,
        },
      };
    }

    return { props: { title: "", isCourseCategory: false, seo: null, staticPage: undefined } };
  }
);
